/**
 * -----------------------------------------------------------------------------
 * Document Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single document of the organization.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HeaderView from "../../../components/admin/headerView";
import { APP_CONFIG } from "../../../constants/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import {
  useEditOrganisationDocumentMutation,
  useOrganisationDocumentsQuery,
  useOrganisationSingleDocumentQuery,
} from "../../../api/network/adminApiServices";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { TDocument, TSingleDocumentPayload } from "../../../api/types/Document";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { getFileTypeByCategory } from "./constants";
import AppSearchBox from "../../../components/searchBox";
import { useDebouncedCallback } from "use-debounce";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import Accordian from "../../../components/accordian";
import ModalEditDocument from "./modalEditDocument";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../role/constants";

const ScreenAdminDetailDocument = () => {
  const { documentId } = useParams<{ documentId: any }>();
  const { t: tToast } = useTranslation("translation", {
    keyPrefix: "admins.documents.detailsPage.toast",
  });
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.documents",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.documents.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { documents: orgDocumentQueryParams } = listingQueryParams;

  // Header to append in file url for viewing
  const HEADERS = {
    // as the file URL is an AWS signed-URL, we don't need to pass the Authorization header
    // "Authorization": preparedAuthHeader(localtorage.getItem(localStorageKeys.accessToken)),
  };
  const { getUserACLAccessLevel } = useACL();
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        documents: { ...orgDocumentQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization documents
  const {
    data: dataOrgDocuments,
    isFetching: isFetchingOrgDocuments,
    error,
  } = useOrganisationDocumentsQuery(orgDocumentQueryParams);
  const { results, count } = dataOrgDocuments || {};

  // fetch single document details
  const {
    data: dataSingleDocument,
    isFetching: isFetchingSingleDocument,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useOrganisationSingleDocumentQuery(
    {
      id: documentId,
    } as TSingleDocumentPayload,
    {
      skip: !documentId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
      ) {
        toast.error(t("toast.document_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.drivers);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  // to re-render the document viewer
  const [docViewerReadyState, setDocViewerReadyState] = useState(false);
  useEffect(() => {
    setDocViewerReadyState(false);
  }, [documentId]);
  useEffect(() => {
    if (!!dataSingleDocument) {
      setDocViewerReadyState(false);
      setTimeout(() => {
        setDocViewerReadyState(true);
      }, 0);
    }
  }, [dataSingleDocument]);

  // document mutations
  const [
    editOrganizationDocumentApiTrigger,
    { isLoading: isLoadingEditDocument },
  ] = useEditOrganisationDocumentMutation();

  const handleEditDocument = () => {
    editOrganizationDocumentApiTrigger({
      id: documentId,
      is_active: !dataSingleDocument?.is_active,
    })
      .unwrap()
      .then(() => {
        toast.success(tToast("document_updated"));
        dispatch(
          setModalsData({ ...modalsState, showEditDocumentModal: false })
        );
      })
      .catch((error) => {
        const errors = !!error?.data
          ? error?.data
          : [tToast("updation_failed")];
        toast.error(errors?.join(" "));
      });
  };

  const onBackButtonClick = () =>
    navigate(routeUrls.dashboardChildren.adminChildren.documents);

  const ErrorView = useMemo(() => {
    return (
      <div>
        <label className="text-field-error-dark">{t("error")}</label>
      </div>
    );
  }, []);

  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${
              isFetchingSingleDocument ? " opacity-40 pointer-events-none" : ""
            }`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgDocumentQueryParams?.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {results?.map((item: TDocument, index: number) => (
                <AdminListingColumnItem
                  key={index}
                  selected={documentId === item.id}
                  onClick={() =>
                    navigate(
                      `${routeUrls.dashboardChildren.adminChildren.documents}/${item.id}`
                    )
                  }
                  title={item.file_name}
                  description={getFileTypeByCategory(
                    item.file_type
                  ).toUpperCase()}
                  asideText={item?.is_active ? t("active") : t("inactive")}
                  bottomText=""
                />
              ))}
            </div>
            {!isFetchingOrgDocuments && (
              <Pagination
                pageSize={orgDocumentQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      drivers: {
                        ...orgDocumentQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(count / orgDocumentQueryParams.page_size)
                    : 1
                }
                forcePage={orgDocumentQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      drivers: {
                        ...orgDocumentQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div className={"col-span-12 xl:col-span-9"}>
            <div className="flex items-center gap-4">
              <div className="flex-grow"></div>
              <AdminFormFieldSubmit
                type="button"
                variant={dataSingleDocument?.is_active ? "danger" : "success"}
                label={
                  dataSingleDocument?.is_active
                    ? t("deactivate")
                    : t("activate")
                }
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showEditDocumentModal: true,
                    })
                  );
                }}
                disabled={
                  isFetchingSingleDocument ||
                  isLoadingEditDocument ||
                  getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DOCUMENT) <
                    ROLE_ACCESS_LEVELS.WRITE
                }
              />
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${
                isFetchingSingleDocument ? "opacity-40" : ""
              }`}
            >
              <Accordian
                title={dataSingleDocument?.file_name || ""}
                openByDefault
              >
                <div className="p-4 border border-gray-100 border-t-0 bg-white">
                  {docViewerReadyState && dataSingleDocument && (
                    <>
                      <DocViewer
                        documents={[
                          {
                            uri: dataSingleDocument?.file,
                            fileType: getFileTypeByCategory(
                              dataSingleDocument?.file_type
                            ),
                          },
                        ]}
                        pluginRenderers={DocViewerRenderers}
                        requestHeaders={HEADERS}
                        config={{
                          pdfZoom: {
                            defaultZoom: 1.1, // 1 as default,
                            zoomJump: 0.2, // 0.1 as default,
                          },
                          pdfVerticalScrollByDefault: true, // false as default
                        }}
                        className="grid grid-cols-12"
                      />
                    </>
                  )}
                </div>
              </Accordian>
            </div>
          </div>
        </div>
      </div>
      <ModalEditDocument
        isActive={dataSingleDocument?.is_active || false}
        handleDeleteAdmin={handleEditDocument}
      />
    </>
  );
};

export default ScreenAdminDetailDocument;
